/*
 * Prophets Hub — First-visit animated onboarding popup (Feb 2026).
 *
 * Mounted once, near the top of the router tree, and self-gates on:
 *   • non-admin path (`isEligibleOnboardingPath`)
 *   • unversioned `prophets_hub_onboarding_seen_v1` localStorage flag
 *
 * The tutorial is 7 visual states — 6 feature demos and a final
 * `Join the Adventure` Discord CTA. A parent-owned four-second
 * timer auto-advances slides 1 through 6; slide 7 halts the
 * timer entirely so the CTA never scrolls itself away. `Next`
 * still walks every state manually, `Back` returns, and any of
 * Escape / X / Skip / final CTA marks the tutorial as seen so
 * it never reappears in that browser.
 *
 * ZERO backend calls. Purely a client-side demo — every visible
 * button (Spin, Submit, Yes/No, obstacle picks) is decorative.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { discordLoginUrl } from "@/lib/pendingInvite";
import {
  hasSeenOnboarding,
  markOnboardingSeen,
  isEligibleOnboardingPath,
  HUB_ONBOARDING_OPEN_EVENT,
} from "@/lib/hubOnboardingSeen";
import {
  SceneDailySpin,
  SceneShare,
  ScenePlay,
  ScenePredict,
  SceneLeaderboard,
  SceneSpend,
  SceneAccess,
} from "./HubOnboardingScenes";
import "./HubOnboarding.css";

// Seven-slide onboarding. Pagination and progress dots are both
// derived from this single source of truth so the counter can never
// drift from the actual number of scenes rendered. Order is the
// canonical presentation sequence — reorder here to reorder the
// whole tutorial.
const SCENES = [
  { id: "predict",    Component: ScenePredict },
  { id: "leader",     Component: SceneLeaderboard },
  { id: "spin",       Component: SceneDailySpin },
  { id: "share",      Component: SceneShare },
  { id: "play",       Component: ScenePlay },
  { id: "spend",      Component: SceneSpend },
  { id: "access",     Component: SceneAccess },
];
const TOTAL_SLIDES = SCENES.length;
// Auto-progression dwell time on every slide except the final one.
// Kept as a module-level constant so the focused test can reference
// the same value without duplicating literals.
export const HBO_AUTO_ADVANCE_MS = 4000;

// Detects `prefers-reduced-motion: reduce` once at mount.
function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    try {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      const on = () => setReduced(mq.matches);
      on();
      mq.addEventListener("change", on);
      return () => mq.removeEventListener("change", on);
    } catch { /* SSR / older browser — leave `false` */ }
  }, []);
  return reduced;
}

// Lock `document.body` scrolling while the popup is open. Restores
// whatever `overflow` was there on close, and safe against React
// double-mount in Strict Mode via a ref counter.
function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

export default function HubOnboarding() {
  const location = useLocation();
  const reduced = useReducedMotion();

  // First-visit gate — computed once on mount so navigating between
  // Hub pages while the tutorial is closed doesn't reopen it.
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    if (!isEligibleOnboardingPath(window.location.pathname)) return false;
    return !hasSeenOnboarding();
  });
  // Re-check when the path changes (invitation deep-link → hub, etc.).
  useEffect(() => {
    if (!isEligibleOnboardingPath(location.pathname)) setOpen(false);
  }, [location.pathname]);

  // Listen for the manual "HOW IT WORKS" button. Manual opens always
  // reset to scene 0 and bypass the seen flag (spec: unlimited reruns).
  useEffect(() => {
    const onOpen = () => {
      setIdx(0);
      setOpen(true);
    };
    window.addEventListener(HUB_ONBOARDING_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(HUB_ONBOARDING_OPEN_EVENT, onOpen);
  }, []);

  const [idx, setIdx] = useState(0);
  const dialogRef = useRef(null);
  useBodyScrollLock(open);

  const dismiss = useCallback(() => {
    markOnboardingSeen();
    setOpen(false);
  }, []);

  // Escape → dismiss (spec).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); dismiss(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  const current = SCENES[idx];
  const slideNumber = idx + 1;
  const isLast = idx === SCENES.length - 1;
  const isFirst = idx === 0;

  // The current viewer's auth state is exposed via a data attribute
  // on <body> by the existing Hub auth bootstrap. We fall back to
  // "unknown" here — the CTA copy handles both branches without a
  // dedicated fetch.
  const authenticated = useMemo(() => {
    try {
      return document.body.dataset?.hubAuth === "authenticated";
    } catch { return false; }
  }, [open]);

  const onNext = () => {
    if (isLast) return dismiss();
    setIdx((i) => Math.min(SCENES.length - 1, i + 1));
  };
  const onBack = () => setIdx((i) => Math.max(0, i - 1));

  // Auto-progression — a single parent-owned timeout advances the
  // slide every `HBO_AUTO_ADVANCE_MS`ms. Rules:
  //   • Runs only while the modal is open and we're not on the final
  //     slide (never loops back to slide 1).
  //   • Any change to `idx` (manual NEXT/BACK) re-runs this effect,
  //     which clears the previous timer and starts a fresh four-second
  //     window on the new slide.
  //   • The cleanup also fires on modal close and unmount, so no
  //     stale timers survive.
  useEffect(() => {
    if (!open) return;
    if (idx >= SCENES.length - 1) return;
    const t = window.setTimeout(() => {
      setIdx((i) => Math.min(SCENES.length - 1, i + 1));
    }, HBO_AUTO_ADVANCE_MS);
    return () => window.clearTimeout(t);
  }, [open, idx]);

  const finalCta = () => {
    if (authenticated) {
      dismiss();
      return;
    }
    // Store the "seen" flag BEFORE navigating away so the visitor
    // never sees the tutorial again once they return post-auth.
    // `discordLoginUrl()` preserves any pending invitation code.
    markOnboardingSeen();
    window.location.href = discordLoginUrl();
  };

  if (!open || !current) return null;

  const SceneComponent = current.Component;

  return createPortal(
    <div className="hbo-root" role="dialog" aria-modal="true"
         aria-labelledby="hbo-title" data-testid="hub-onboarding">
      <button type="button"
              className="hbo-overlay"
              onClick={dismiss}
              aria-label="Close onboarding"
              tabIndex={-1} />
      <div className="hbo-dialog" ref={dialogRef}>
        <header className="hbo-header">
          <button type="button"
                  className="hbo-close"
                  onClick={dismiss}
                  aria-label="Close"
                  data-testid="hbo-close-btn">
            ×
          </button>
        </header>
        <div className="hbo-stage" key={current.id}>
          <SceneComponent animate reducedMotion={reduced}
                          authenticated={authenticated}
                          onFinalCta={finalCta} />
        </div>
        <footer className="hbo-controls">
          <div className="hbo-controls-left">
            {isFirst ? (
              <button type="button" className="hbo-ctrl-btn hbo-ctrl-btn--ghost"
                      onClick={dismiss}
                      data-testid="hbo-skip-btn">
                SKIP
              </button>
            ) : (
              <button type="button" className="hbo-ctrl-btn hbo-ctrl-btn--ghost"
                      onClick={onBack}
                      data-testid="hbo-back-btn">
                ← BACK
              </button>
            )}
          </div>
          <div className="hbo-controls-center" data-testid="hbo-progress">
            <div className="hbo-progress-num">
              <b>{slideNumber}</b> <span>/</span> <b>{TOTAL_SLIDES}</b>
            </div>
            <div className="hbo-progress-dots" aria-hidden="true">
              {SCENES.map((s, i) => (
                <span key={s.id}
                      className={`hbo-progress-dot ${i === idx ? "is-active" : ""}`} />
              ))}
            </div>
          </div>
          <div className="hbo-controls-right">
            {isLast ? null : (
              <button type="button" className="hbo-ctrl-btn hbo-ctrl-btn--primary"
                      onClick={onNext}
                      data-testid="hbo-next-btn">
                NEXT →
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
